/**
 * Générateur — Attendu 944 (5e, Espace et géométrie / Repérage)
 * Libellé officiel : « Sur une droite graduée : lire l'abscisse d'un point
 * donné ; placer un point d'abscisse donnée. »
 *
 * Plan de cartes (campagne 5e, mise en conformité avec la spécification
 * `specifications-tanoed/5e/944.json`, 2026-09-21) :
 *
 *   Carte « Donner les abscisses » (id abscisses) — LECTURE. Une droite
 *     graduée conforme au modèle de la spec (bord gauche strictement négatif,
 *     7 intervalles principaux étiquetés, 10 sous-graduations muettes par
 *     intervalle, 0 dans la fenêtre sans en être le bord), 3 points lettrés
 *     (lettres consécutives, de gauche à droite) qui ne tombent jamais sur une
 *     graduation principale. Un format par niveau (entiers, dixièmes,
 *     centièmes, millièmes), un format mélange (niveau 5 : le pas change à
 *     chaque question), un drag et le QCM de l'attendu (1 point, niveaux 2 à 4).
 *
 *   Carte « Placer des points d'abscisse donnée » (id placer) — PLACEMENT.
 *     Mêmes droites que la lecture, format interactif placer_points (le snap
 *     sur la graduation la plus proche réalise la tolérance d'une demi
 *     sous-graduation de la spec). Un format par niveau, un format mélange,
 *     et la variante lettrée (6 graduations lettrées pour 3 points, les 3
 *     leurres étant les positions des erreurs visées).
 *
 *   Carte « Sur une demi-droite graduée » (id demi_droite) — réglage
 *     transverse de la spec : demi-droite d'origine 0 au bord, pour que le
 *     contraste avec la droite reste visible. Lecture (saisie), placement,
 *     QCM (niveaux mélangés).
 *
 * Spécification appliquée :
 *   - bord gauche du tracé : -6 à -1 (pas 1), -0,6 à -0,2 (pas 0,1),
 *     -0,06 à -0,02 (pas 0,01 — voir « non tranché ») ; toujours < 0, 0 dans
 *     la fenêtre ; demi-droite : 0 au bord ;
 *   - fenêtre : 7 intervalles principaux, 10 sous-graduations par intervalle
 *     (aucune au niveau entier) ; graduations principales seules étiquetées
 *     (au niveau entier : seules 0 et 1) ;
 *   - rang principal des points : plages disjointes 0-2, 3-4, 5-6 ; sous-rang
 *     1 à 9 (1 à 3 pour le troisième) ; jamais sur une graduation principale ;
 *     au niveau entier, 0 et 1 ne sont jamais des points à lire ;
 *   - abscisses déduites, calculées en ENTIERS de sous-graduations, converties
 *     à l'affichage (virgule française, jamais de zéro final) ;
 *   - 1 point (QCM) ou 3 points (saisie, placement) par droite ;
 *   - exclusions : aucun pas en tiers, quarts ou moitiés ; aucune abscisse
 *     tirée directement.
 *
 * Erreurs visées → mots-clés (codes des distracteurs `_meta.params.distracteurs[].code`,
 * ou `_meta.params.erreursVisees` d'une carte qui provoque l'erreur) :
 *   1. Compter les traits depuis le bord gauche et rendre un rang
 *      → rang_depuis_bord
 *   2. Prendre l'extrémité gauche pour l'origine (abscisses toutes positives)
 *      → origine_au_bord
 *   3. Rabattre le point sur la graduation principale la plus proche
 *      → rabattement_graduation_principale
 *   4. Conserver le pas de la droite précédente sans relire les étiquettes
 *      → pas_precedent
 *   5. Croire que le nombre de décimales indique la graduation à utiliser
 *      → decimales_dictent_graduation
 *   6. Sur la demi-droite, transposer une lecture apprise sur la droite (ou l'inverse)
 *      → transposition_droite_demi_droite
 *
 * Graduation → cartes :
 *   niveau 1 (entiers, seules 0 et 1 étiquetées) : abscisses_entiers, placer_entiers
 *   niveau 2 (dixièmes)   : abscisses_dixiemes, placer_dixiemes, placer_lettres
 *   niveau 3 (centièmes)  : abscisses_centiemes, placer_centiemes, placer_lettres
 *   niveau 4 (millièmes)  : abscisses_milliemes, placer_milliemes
 *   niveau 5 (mélange)    : abscisses_texte_trou_saisie, abscisses_texte_trou_drag,
 *                           abscisses_qcm_unique, placer_melange
 *   réglage demi-droite   : demi_droite_* (niveaux mélangés)
 *
 * Non tranché (appliqué tel quel, signalé dans le rapport) :
 *   - le bord gauche « max -0,2 » est incompatible avec le niveau 4 (fenêtre de
 *     0,07 : 0 n'y entrerait jamais) ; on applique l'exclusion « 0 dans la
 *     fenêtre » et un bord gauche de -0,06 à -0,02 à ce niveau ;
 *   - au niveau entier, les plages disjointes (0-2, 3-4, 5-6) sont impossibles
 *     quand 0 et 1 occupent la plage centrale : trois rangs distincts (séparés
 *     d'au moins une graduation principale, ce que la spec exige) ;
 *   - le rendu natif `numberline` apparie un point à une graduation à 0,001
 *     près : au niveau 4 la lecture est dessinée en SVG par le gen.
 *
 * Invariants techniques : arithmétique en unités de sous-graduation (entiers),
 * `frU` pour tout affichage ; convention d'écriture « - » clavier, jamais de
 * « + » ; placer_points : bonne_reponse = écriture canonique du snap.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, basePayload,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 944;

const L_ABSCISSES = 'Donner les abscisses';
const L_PLACER = 'Placer des points d\'abscisse donnée';
const L_DEMI = 'Sur une demi-droite graduée';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Codes des erreurs visées (table dans l'en-tête)
const E = {
    RANG: 'rang_depuis_bord',
    ORIGINE: 'origine_au_bord',
    RABATTEMENT: 'rabattement_graduation_principale',
    PAS: 'pas_precedent',
    DECIMALES: 'decimales_dictent_graduation',
    TRANSPOSITION: 'transposition_droite_demi_droite',
};

/** Niveaux de la spec : sub = sous-graduations par intervalle principal,
 *  den = dénominateur d'une unité de sous-graduation (1 unité = 1/den). */
const NIVEAUX = {
    1: { sub: 1, den: 1, decimales: 0, nom: 'entiers' },
    2: { sub: 10, den: 10, decimales: 1, nom: 'dixiemes' },
    3: { sub: 10, den: 100, decimales: 2, nom: 'centiemes' },
    4: { sub: 10, den: 1000, decimales: 3, nom: 'milliemes' },
};
const N_INTERVALLES = 7; // rang principal max 6 + sous-rang ⇒ 7 intervalles

// Lettres des points : consécutives, sans I ni O (confusion 1 / 0)
const NOMS_POINTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K'];
// Graduations lettrées (variante lettrée) : minuscules distinctes
const LETTRES_GRAD = ['m', 'n', 'p', 'r', 's', 't', 'u', 'v'];

/**
 * ⚠️ Cette consigne disait « Écris chaque abscisse sans le signe + : 3 ou -2,5 ».
 *
 * Deux défauts. Le « + » collé aux deux points se lisait comme de la ponctuation, si
 * bien qu'on comprenait « sans le signe » tout court, et la correction qui affiche
 * −0,012 paraissait se contredire (retour prof 2026-09-25). Et surtout elle interdisait
 * ce que la correction ACCEPTE : le juge compare les nombres par leur valeur, « +0,12 »
 * passe. Une consigne ne doit pas être plus sévère que le barème.
 *
 * La formulation reprend celle des gens 910, 914, 916, 917 et 918, déjà en service.
 */
const CONSIGNE_ECRITURE = '(Écris chaque abscisse en écriture décimale, par exemple 3 '
    + 'ou -2,5 ; un nombre positif peut s\'écrire avec ou sans le signe +.)';
const CONSIGNE_ECRITURE_DEMI = '(Écris chaque abscisse avec une virgule : 3 ou 2,5)';

/** Unités de sous-graduation → écriture française canonique (jamais de zéro final). */
const frU = (u, den) => {
    const s = u < 0 ? '-' : '';
    const a = Math.abs(u);
    const ent = Math.floor(a / den);
    const nbDec = String(den).length - 1;
    const frac = nbDec ? String(a % den).padStart(nbDec, '0').replace(/0+$/, '') : '';
    return s + (frac ? `${ent},${frac}` : String(ent));
};
/** Unités → valeur numérique (pour les configs numberline). */
const val = (u, den) => Math.round((u / den) * 1e6) / 1e6;

/** Lettres consécutives de l'alphabet, point de départ aléatoire. */
const lettresConsecutives = (n) => {
    const debut = randInt(0, NOMS_POINTS.length - n);
    return NOMS_POINTS.slice(debut, debut + n);
};

// ═════════ Tirage d'une droite et de ses points ═════════

/**
 * Droite d'un niveau donné : { niveau, sub, den, g (bord gauche en unités),
 * fin, demi, reperes (unités étiquetées) }.
 * Droite : g = -k × sub, k ∈ [1;6] (pas 1) ou [2;6] (pas 0,1 et 0,01), 0 dans
 * la fenêtre sans en être le bord. Demi-droite : g = 0.
 */
const tirerDroite = (niveau, demi = false) => {
    const { sub, den } = NIVEAUX[niveau];
    const k = demi ? 0 : (niveau <= 2 ? randInt(1, 6) : randInt(2, 6));
    const g = k === 0 ? 0 : -k * sub;
    const fin = g + N_INTERVALLES * sub;
    const reperes = niveau === 1
        ? [0, sub]
        : Array.from({ length: N_INTERVALLES + 1 }, (_, i) => g + i * sub);
    return { niveau, sub, den, g, fin, demi, reperes };
};

const PLAGES = [[0, 2], [3, 4], [5, 6]];

/**
 * n points (1 ou 3) sur la droite, en unités, triés de gauche à droite.
 * Niveaux 2 à 4 : rang principal dans des plages disjointes, sous-rang 1..9
 * (1..3 pour la troisième plage). Niveau 1 : rangs entiers distincts (1 à 6,
 * jamais l'extrémité du tracé), jamais les graduations 0 et 1.
 */
const tirerPoints = (d, n) => {
    const { niveau, sub, g } = d;
    if (niveau === 1) {
        const interdits = new Set([0, sub].map(u => (u - g) / sub)); // rangs de 0 et 1
        const rangs = [];
        // rang 1 à 6 : au niveau entier, le rang 0 serait l'extrémité du tracé
        for (let r = 1; r <= 6; r++) if (!interdits.has(r)) rangs.push(r);
        return shuffle(rangs).slice(0, n).sort((a, b) => a - b)
            .map(r => ({ r, s: 0, u: g + r * sub }));
    }
    const plages = n === 3 ? PLAGES : shuffle(PLAGES).slice(0, n).sort((a, b) => a[0] - b[0]);
    return plages.map(([lo, hi]) => {
        const r = randInt(lo, hi);
        const s = lo === 5 ? randInt(1, 3) : randInt(1, 9);
        return { r, s, u: g + r * sub + s };
    });
};

/** Le niveau au hasard parmi une liste (cartes « mélange »). */
const niveauParmi = (liste) => randomChoice(liste);

// ═════════ Figures ═════════

/** Élément numberline natif (niveaux 1 à 3). */
const elementNumberline = (d, points) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: val(d.g, d.den), end: val(d.fin, d.den), step: val(1, d.den),
            reperes: d.reperes.map(u => val(u, d.den)),
            points: points.map(p => ({ value: val(p.u, d.den), label: p.label, showValue: false })),
            lineType: d.demi ? 'half' : 'full',
            showArrow: true,
            showStart: d.niveau !== 1,
            showEnd: d.niveau !== 1,
        },
    }],
});

/** Figure SVG dessinée par le gen (niveau 4 : le rendu natif apparie les
 *  points à 0,001 près, trop grossier pour un pas de 0,001). */
const svgDroite = (d, points) => {
    const W = 640, H = 96, lineY = 52, padL = 46, padR = 46;
    const nTicks = N_INTERVALLES * d.sub;
    const x = (u) => padL + ((u - d.g) / nTicks) * (W - padL - padR);
    const reperes = new Set(d.reperes);
    let s = '';
    // ligne + flèches (deux flèches pour la droite, une seule pour la demi-droite)
    const x0 = d.demi ? padL : padL - 26;
    s += `<line x1="${x0}" y1="${lineY}" x2="${W - padR + 26}" y2="${lineY}" stroke="currentColor" stroke-width="1.2"/>`;
    s += `<polygon points="${W - padR + 34},${lineY} ${W - padR + 24},${lineY - 5} ${W - padR + 24},${lineY + 5}" fill="currentColor"/>`;
    if (!d.demi) {
        s += `<polygon points="${padL - 34},${lineY} ${padL - 24},${lineY - 5} ${padL - 24},${lineY + 5}" fill="currentColor"/>`;
    }
    for (let i = 0; i <= nTicks; i++) {
        const u = d.g + i;
        const principale = i % d.sub === 0;
        const h = principale ? 8 : 4;
        const xx = x(u).toFixed(1);
        s += `<line x1="${xx}" y1="${lineY - h}" x2="${xx}" y2="${lineY + h}" stroke="currentColor" stroke-width="${principale ? 1.4 : 1}"/>`;
        if (reperes.has(u)) {
            s += `<text x="${xx}" y="${lineY + 24}" text-anchor="middle" font-size="12" font-family="sans-serif" fill="currentColor">${frU(u, d.den)}</text>`;
        }
    }
    points.forEach(p => {
        const xx = x(p.u).toFixed(1);
        s += `<circle cx="${xx}" cy="${lineY}" r="4" fill="currentColor"/>`;
        s += `<text x="${xx}" y="${lineY - 14}" text-anchor="middle" font-size="14" font-weight="bold" font-family="sans-serif" fill="currentColor">${p.label}</text>`;
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${s}</svg>`;
};

const elementImage = (d, points) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: {
            imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svgDroite(d, points)),
            maxWidth: 'large',
            alt: d.demi ? 'Demi-droite graduée' : 'Droite graduée',
        },
    }],
});

const figure = (d, points) => (d.niveau === 4 ? elementImage(d, points) : elementNumberline(d, points));

const nomSupport = (d) => (d.demi ? 'la demi-droite graduée' : 'la droite graduée');

// ═════════ Distracteurs (erreurs visées) ═════════

/**
 * Candidats-distracteurs d'un point, chacun avec le code de l'erreur visée.
 * Ordre : origine_au_bord d'abord (au niveau entier, il coïncide avec le
 * rang depuis le bord ; la dédoublonnage garde le premier code).
 */
const candidatsDistracteurs = (d, p) => {
    const { sub, den, g } = d;
    const c = [];
    // 2. bord gauche pris pour l'origine : abscisse lue depuis le bord (positive)
    c.push({ valeur: frU(p.u - g, den), code: E.ORIGINE });
    // 1. rang brut : nombre de traits comptés depuis le bord gauche
    c.push({ valeur: String(p.r * sub + p.s), code: E.RANG });
    if (sub > 1) {
        // 3. rabattement sur la graduation principale la plus proche
        const principale = g + (p.s <= 5 ? p.r : p.r + 1) * sub;
        c.push({ valeur: frU(principale, den), code: E.RABATTEMENT });
        // 4. pas de la droite précédente : chaque sous-graduation lue comme un pas principal
        c.push({ valeur: frU(g + p.r * sub + p.s * sub, den), code: E.PAS });
    }
    // 6. transposition droite / demi-droite : signe oublié (droite) ou ajouté (demi-droite)
    if (d.demi) c.push({ valeur: frU(-p.u, den), code: E.TRANSPOSITION });
    else if (p.u < 0) c.push({ valeur: frU(-p.u, den), code: E.TRANSPOSITION });
    return c;
};

/** Dédoublonne les candidats (jamais la bonne réponse, jamais deux valeurs égales). */
const distracteursDistincts = (candidats, corrects) => {
    const vus = new Set(corrects);
    return candidats.filter(c => {
        if (vus.has(c.valeur)) return false;
        vus.add(c.valeur);
        return true;
    });
};

// ═════════ LECTURE : « Donner les abscisses » ═════════

const genLectureSaisie = (niveaux, demi, label, erreursVisees) => () => {
    const d = tirerDroite(niveauParmi(niveaux), demi);
    const lettres = lettresConsecutives(3);
    const points = tirerPoints(d, 3).map((p, i) => ({ ...p, label: lettres[i], abscisse: frU(p.u, d.den) }));
    const phrase = points.map(p => `${p.label}( {{${p.abscisse}}} )`).join('\n'); // un point par ligne
    const payload = makeTexteTrou(phrase,
        `<p><b>Donne l'abscisse de chaque point de ${nomSupport(d)}.</b> ${demi ? CONSIGNE_ECRITURE_DEMI : CONSIGNE_ECRITURE}</p>`,
        { ...opts(label), mode: 'saisie', elements_additionnels: figure(d, points) });
    payload._meta.params = { tache: 'lire', droite: d, points, erreursVisees };
    return payload;
};

const genLectureDrag = (niveaux, demi, label) => () => {
    const d = tirerDroite(niveauParmi(niveaux), demi);
    const lettres = lettresConsecutives(3);
    const points = tirerPoints(d, 3).map((p, i) => ({ ...p, label: lettres[i], abscisse: frU(p.u, d.den) }));
    const phrase = points.map(p => `${p.label}( {{${p.abscisse}}} )`).join('\n');
    const candidats = distracteursDistincts(
        shuffle(points.flatMap(p => candidatsDistracteurs(d, p))),
        points.map(p => p.abscisse));
    const gardes = filtrerDistracteurs(phrase, candidats.map(c => c.valeur)).slice(0, 4);
    const distracteurs = candidats.filter(c => gardes.includes(c.valeur));
    const payload = makeTexteTrou(phrase,
        `<p><b>Glisse l'abscisse de chaque point de ${nomSupport(d)}.</b></p>`,
        {
            ...opts(label), mode: 'drag',
            distracteurs: gardes,
            elements_additionnels: figure(d, points),
        });
    payload._meta.params = { tache: 'lire', droite: d, points, distracteurs, erreursVisees: [E.PAS] };
    return payload;
};

/** QCM (un seul point marqué) : 3 distracteurs tirés parmi les erreurs visées. */
const genLectureQcm = (niveaux, demi, label) => () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerDroite(niveauParmi(niveaux), demi);
        const [lettre] = lettresConsecutives(1);
        const points = tirerPoints(d, 1).map(p => ({ ...p, label: lettre, abscisse: frU(p.u, d.den) }));
        const p = points[0];
        const distracteurs = distracteursDistincts(shuffle(candidatsDistracteurs(d, p)), [p.abscisse]).slice(0, 3);
        if (distracteurs.length < 3) continue; // nouveau tirage complet, jamais de bouche-trou
        const payload = makeQcm(shuffle([p.abscisse, ...distracteurs.map(c => c.valeur)]), p.abscisse,
            `<p><b>Quelle est l'abscisse du point ${p.label} sur ${nomSupport(d)} ?</b></p>`,
            { ...opts(label), elements_additionnels: figure(d, points) });
        payload._meta.params = { tache: 'lire', droite: d, points, distracteurs, erreursVisees: [E.PAS] };
        return payload;
    }
    throw new Error('gen_944 : impossible de réunir 3 distracteurs distincts (niveaux 2 à 4 attendus)');
};

// ═════════ PLACEMENT : « Placer des points d'abscisse donnée » ═════════

/** Écriture canonique du snap élève (Math.round(v/step)*step, 3 décimales, virgule). */
const canonSnap = (u, d) => {
    const step = val(1, d.den);
    const v = Math.round(val(u, d.den) / step) * step;
    return String(Math.round(v * 1000) / 1000).replace('.', ',');
};

const genPlacer = (niveaux, demi, label, erreursVisees) => () => {
    const d = tirerDroite(niveauParmi(niveaux), demi);
    const lettres = shuffle(lettresConsecutives(3)); // l'ordre des lettres ne trahit pas l'ordre des points
    const points = tirerPoints(d, 3).map((p, i) => ({ ...p, label: lettres[i], abscisse: frU(p.u, d.den) }));
    const bonne = {};
    points.forEach(p => {
        const canon = canonSnap(p.u, d);
        if (canon !== p.abscisse) throw new Error(`gen_944 : abscisse ${p.abscisse} non canonique (${canon})`);
        bonne[p.label] = canon;
    });
    const ordre = shuffle(points);
    const consigne = ordre.map(p => `${p.label}(${p.abscisse})`).join(' ; ');
    const payload = basePayload('association',
        `<p><b>Place les points ${consigne} sur ${nomSupport(d)}.</b></p>`,
        {
            subType: 'placer_points',
            numberline: {
                start: val(d.g, d.den), end: val(d.fin, d.den), step: val(1, d.den),
                reperes: d.reperes.map(u => val(u, d.den)),
            },
            pointsToPlace: ordre.map(p => p.label),
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(label));
    payload._meta.params = { tache: 'placer', droite: d, points, erreursVisees };
    return payload;
};

/** Variante lettrée (niveaux 2 et 3) : 6 graduations lettrées pour 3 points,
 *  les 3 leurres sont les positions des erreurs visées. */
const genPlacerLettres = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerDroite(niveauParmi([2, 3]));
        const points = tirerPoints(d, 3).map(p => ({ ...p, abscisse: frU(p.u, d.den) }));
        const cibles = new Set(points.map(p => p.u));
        const leurres = [];
        const vus = new Set(cibles);
        shuffle(points.flatMap(p => {
            const { sub, g } = d;
            return [
                { u: p.u - g, code: E.ORIGINE },
                { u: g + (p.s <= 5 ? p.r : p.r + 1) * sub, code: E.RABATTEMENT },
                { u: g + p.r * sub + p.s * sub, code: E.PAS },
                { u: -p.u, code: E.TRANSPOSITION },
            ];
        })).forEach(l => {
            if (leurres.length < 3 && l.u > d.g && l.u < d.fin && !vus.has(l.u)) {
                vus.add(l.u);
                leurres.push(l);
            }
        });
        if (leurres.length < 3) continue;
        const positions = [...points.map(p => ({ u: p.u })), ...leurres].sort((a, b) => a.u - b.u);
        const lettres = shuffle(LETTRES_GRAD).slice(0, positions.length);
        positions.forEach((pos, i) => { pos.lettre = lettres[i]; });
        const lettreDe = (u) => positions.find(pos => pos.u === u).lettre;
        const noms = lettresConsecutives(3);
        const pointsNommes = points.map((p, i) => ({ ...p, label: noms[i] }));
        const phrase = pointsNommes
            .map(p => `${p.label}(${p.abscisse}) : graduation {{${lettreDe(p.u)}}}`)
            .join('\n'); // un point par ligne
        const distracteurs = leurres.map(l => ({ valeur: lettreDe(l.u), code: l.code }));
        const payload = makeTexteTrou(phrase,
            '<p><b>Chaque lettre repère une graduation de la droite. Trouve la graduation de chaque point.</b></p>',
            {
                ...opts(L_PLACER), mode: 'drag',
                distracteurs: filtrerDistracteurs(phrase, distracteurs.map(l => l.valeur)),
                elements_additionnels: elementNumberline(d,
                    positions.map(pos => ({ u: pos.u, label: pos.lettre }))),
            });
        payload._meta.params = {
            tache: 'placer', droite: d, points: pointsNommes, distracteurs,
            erreursVisees: [E.DECIMALES],
        };
        return payload;
    }
    throw new Error('gen_944 : variante lettrée sans 3 leurres distincts');
};

// ─── Registre ────────────────────────────────────────────────

const GENERATORS = {
    // Lecture (droite graduée)
    abscisses_entiers_texte_trou_saisie: genLectureSaisie([1], false, L_ABSCISSES, [E.ORIGINE, E.RANG]),
    abscisses_dixiemes_texte_trou_saisie: genLectureSaisie([2], false, L_ABSCISSES, [E.ORIGINE, E.RABATTEMENT]),
    abscisses_centiemes_texte_trou_saisie: genLectureSaisie([3], false, L_ABSCISSES, [E.ORIGINE, E.RABATTEMENT]),
    abscisses_milliemes_texte_trou_saisie: genLectureSaisie([4], false, L_ABSCISSES, [E.ORIGINE, E.RABATTEMENT]),
    abscisses_texte_trou_saisie: genLectureSaisie([1, 2, 3, 4], false, L_ABSCISSES, [E.PAS, E.ORIGINE]),
    abscisses_texte_trou_drag: genLectureDrag([1, 2, 3, 4], false, L_ABSCISSES),
    abscisses_qcm_unique: genLectureQcm([2, 3, 4], false, L_ABSCISSES),
    // Placement (droite graduée)
    placer_entiers_placer_points: genPlacer([1], false, L_PLACER, [E.ORIGINE]),
    placer_dixiemes_placer_points: genPlacer([2], false, L_PLACER, [E.ORIGINE, E.RABATTEMENT]),
    placer_centiemes_placer_points: genPlacer([3], false, L_PLACER, [E.ORIGINE, E.RABATTEMENT]),
    placer_milliemes_placer_points: genPlacer([4], false, L_PLACER, [E.ORIGINE, E.RABATTEMENT]),
    placer_melange_placer_points: genPlacer([1, 2, 3, 4], false, L_PLACER, [E.PAS, E.DECIMALES]),
    placer_lettres_texte_trou_drag: genPlacerLettres,
    // Demi-droite graduée (réglage transverse)
    demi_droite_texte_trou_saisie: genLectureSaisie([1, 2, 3, 4], true, L_DEMI, [E.TRANSPOSITION, E.PAS]),
    demi_droite_placer_points: genPlacer([1, 2, 3, 4], true, L_DEMI, [E.TRANSPOSITION, E.PAS]),
    demi_droite_qcm_unique: genLectureQcm([2, 3, 4], true, L_DEMI),
};

const FORMAT_LABELS = {
    abscisses_entiers_texte_trou_saisie: 'Saisie (entiers)',
    abscisses_dixiemes_texte_trou_saisie: 'Saisie (dixièmes)',
    abscisses_centiemes_texte_trou_saisie: 'Saisie (centièmes)',
    abscisses_milliemes_texte_trou_saisie: 'Saisie (millièmes)',
    abscisses_texte_trou_saisie: 'Saisie (pas mélangés)',
    abscisses_texte_trou_drag: 'Drag (pas mélangés)',
    abscisses_qcm_unique: 'QCM',
    placer_entiers_placer_points: 'Placer (entiers)',
    placer_dixiemes_placer_points: 'Placer (dixièmes)',
    placer_centiemes_placer_points: 'Placer (centièmes)',
    placer_milliemes_placer_points: 'Placer (millièmes)',
    placer_melange_placer_points: 'Placer (pas mélangés)',
    placer_lettres_texte_trou_drag: 'Graduations lettrées',
    demi_droite_texte_trou_saisie: 'Lire (saisie)',
    demi_droite_placer_points: 'Placer',
    demi_droite_qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k] || k }));

export const CATEGORIES = [
    { id: 'abscisses', label: L_ABSCISSES, formatsList: buildFormats('abscisses') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
    { id: 'demi_droite', label: L_DEMI, formatsList: buildFormats('demi_droite') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
